<template>
  <div class="mobile-invite-history">
    <div class="history-backdrop" aria-hidden="true" @click="$emit('close')" />
    <section ref="sheet" class="history-sheet" role="dialog" aria-modal="true" :aria-label="title" tabindex="-1" @keydown.tab="trapTab">
      <header class="history-header">
        <span class="history-title">{{ title }}</span>
        <button ref="closeButton" type="button" class="history-close" :aria-label="$t('common.close')" @click="$emit('close')">
          <el-icon><Close /></el-icon>
        </button>
      </header>
      <slot />
    </section>
  </div>
</template>

<script>
import { Close } from '@element-plus/icons-vue'

export default {
  name: 'MobileInviteHistorySheet',
  components: { Close },
  props: { title: { type: String, required: true } },
  emits: ['close'],
  methods: {
    focusClose() {
      this.$refs.closeButton?.focus({ preventScroll: true })
    },
    trapTab(event) {
      const sheet = this.$refs.sheet
      if (!sheet) return
      const targets = Array.from(sheet.querySelectorAll('button, [href], input, select, textarea, [tabindex]')).filter(element => {
        if (element.tabIndex < 0 || element.matches(':disabled') || element.closest('[inert], [aria-hidden="true"]') || !element.getClientRects().length) return false
        const style = window.getComputedStyle(element)
        return style.display !== 'none' && style.visibility !== 'hidden'
      })
      const first = targets[0]
      const last = targets[targets.length - 1]
      const active = document.activeElement
      if (!first) {
        event.preventDefault()
        sheet.focus({ preventScroll: true })
      } else if (!targets.includes(active) || (event.shiftKey ? active === first : active === last)) {
        event.preventDefault()
        const target = event.shiftKey ? last : first
        target.focus({ preventScroll: true })
      }
      event.stopPropagation()
    }
  }
}
</script>

<style lang="scss" scoped>
.mobile-invite-history {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-end;
  overflow: hidden;
}

.history-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.42);
}

.history-sheet {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: min(72vh, 580px);
  height: min(72dvh, 580px);
  max-height: calc(100% - max(16px, env(safe-area-inset-top, 0px)));
  min-height: 0;
  padding-bottom: env(safe-area-inset-bottom, 0px);
  box-sizing: border-box;
  border-radius: 16px 16px 0 0;
  background: var(--agst-bg-color, #fff);
}

.history-header {
  position: relative;
  display: flex;
  flex: 0 0 auto;
  min-height: 56px;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid var(--agst-border-color-lighter, #ebeef5);
  color: var(--agst-text-color-primary, #303133);
}

.history-title {
  min-width: 0;
  padding: 8px 60px;
  overflow-wrap: anywhere;
  font-size: 18px;
  font-weight: 600;
}

.history-close {
  position: absolute;
  top: 6px;
  right: max(10px, env(safe-area-inset-right, 0px));
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--agst-text-color-secondary, #909399);
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--agst-color-primary, #2d66f0);
    outline-offset: -2px;
  }
}

:deep(.history-body) { min-height: 0; }
:deep(.history-empty) { min-height: 0; }
</style>
